/**
 * YQ Search Panel — header-below overlay (Overlay group).
 * Open via [aria-controls="yq-search-panel"]; reuses theme <predictive-search>.
 */
(() => {
  if (customElements.get('yq-search-panel')) return;

  const LOCK_CLASS = 'lock';
  let lockCount = 0;

  class YqSearchPanel extends HTMLElement {
    connectedCallback() {
      this._onToggleClick = this._onToggleClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onSectionSelect = this._onSectionSelect.bind(this);
      this._onSectionDeselect = this._onSectionDeselect.bind(this);
      this._onMegaToggle = this._onMegaToggle.bind(this);
      this._onInputChange = this._onInputChange.bind(this);
      this._onFormReset = this._onFormReset.bind(this);
      this._syncOffset = this._syncOffset.bind(this);

      this._sheet = this.querySelector('[data-yq-search-sheet]');
      this._backdrop = this.querySelector('[data-yq-search-backdrop]');
      this._closeButtons = Array.from(this.querySelectorAll('[data-yq-search-close]'));
      this._input = this.querySelector('input[name="q"]');
      this._form = this.querySelector('form');

      document.addEventListener('click', this._onToggleClick, true);
      document.addEventListener('keydown', this._onKeydown);
      this._backdrop?.addEventListener('click', this._onBackdropClick);
      this._closeButtons.forEach((btn) => btn.addEventListener('click', this._onCloseClick));
      this._input?.addEventListener('input', this._onInputChange);
      this._form?.addEventListener('reset', this._onFormReset);
      document.addEventListener('toggle', this._onMegaToggle, true);

      this._shopifySection = this.closest('.shopify-section');
      if (Shopify.designMode && this.hasAttribute('handle-section-events') && this._shopifySection) {
        this._shopifySection.addEventListener('shopify:section:select', this._onSectionSelect);
        this._shopifySection.addEventListener('shopify:section:deselect', this._onSectionDeselect);
        this._shopifySection.addEventListener('shopify:section:unload', () => this.remove());
      }

      if (!this.hasAttribute('open')) {
        this.setAttribute('inert', '');
      }

      this._syncOffset();
      this._syncQueryState();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onToggleClick, true);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('toggle', this._onMegaToggle, true);
      this._backdrop?.removeEventListener('click', this._onBackdropClick);
      this._closeButtons.forEach((btn) => btn.removeEventListener('click', this._onCloseClick));
      this._input?.removeEventListener('input', this._onInputChange);
      this._form?.removeEventListener('reset', this._onFormReset);
      this._shopifySection?.removeEventListener('shopify:section:select', this._onSectionSelect);
      this._shopifySection?.removeEventListener('shopify:section:deselect', this._onSectionDeselect);
      this._unbindOffsetListeners();
      this._unlock();
    }

    get open() {
      return this.hasAttribute('open');
    }

    show() {
      if (this.open) return;
      this._closeMegaMenus();
      this._syncOffset();
      this.setAttribute('open', '');
      this.removeAttribute('inert');
      this._syncControls(true);
      this._lock();
      this._bindOffsetListeners();
      requestAnimationFrame(() => {
        this._syncOffset();
        this._input?.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('yq-search:open', { bubbles: true }));
    }

    hide() {
      if (!this.open) return;
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      this._syncControls(false);
      this._unbindOffsetListeners();
      this._unlock();
      this._form?.reset();
      this._syncQueryState();
      this.dispatchEvent(new CustomEvent('yq-search:close', { bubbles: true }));
    }

    /**
     * Idle vs query layout: half viewport vs full remaining + scroll.
     */
    _syncQueryState() {
      const hasQuery = Boolean(this._input?.value?.trim());
      this.toggleAttribute('data-yq-has-query', hasQuery);
    }

    _onInputChange() {
      this._syncQueryState();
    }

    _onFormReset() {
      requestAnimationFrame(() => this._syncQueryState());
    }

    /**
     * Place panel flush under visible announcement + header (never covering nav).
     */
    _syncOffset() {
      let top = 0;

      const announcement = document.querySelector('height-observer[variable="announcement-bar"]')?.closest('.shopify-section');
      if (announcement) {
        const rect = announcement.getBoundingClientRect();
        if (rect.bottom > 0) top = Math.max(top, rect.bottom);
      }

      const headerObserver = document.querySelector('height-observer[variable="header"]');
      const headerSection = headerObserver?.closest('.shopify-section');
      if (headerSection) {
        const rect = headerSection.getBoundingClientRect();
        if (rect.bottom > 0) top = Math.max(top, rect.bottom);
      }

      if (top <= 0) {
        const styles = getComputedStyle(document.documentElement);
        const announcementHeight = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
        const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 0;
        top = announcementHeight + headerHeight;
      }

      const value = `${Math.round(top)}px`;
      this.style.setProperty('--yq-search-top', value);
      this._shopifySection?.style.setProperty('--yq-search-top', value);
    }

    _bindOffsetListeners() {
      if (this._offsetBound) return;
      this._offsetBound = true;
      window.addEventListener('resize', this._syncOffset, { passive: true });
      window.addEventListener('scroll', this._syncOffset, { passive: true });
    }

    _unbindOffsetListeners() {
      if (!this._offsetBound) return;
      this._offsetBound = false;
      window.removeEventListener('resize', this._syncOffset);
      window.removeEventListener('scroll', this._syncOffset);
    }

    _syncControls(expanded) {
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => {
        el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    }

    _lock() {
      if (this._locked) return;
      this._locked = true;
      lockCount += 1;
      document.documentElement.classList.add(LOCK_CLASS);
    }

    _unlock() {
      if (!this._locked) return;
      this._locked = false;
      lockCount = Math.max(0, lockCount - 1);
      document.documentElement.classList.toggle(LOCK_CLASS, lockCount > 0);
    }

    _closeMegaMenus() {
      document.querySelectorAll('details[is="mega-menu-disclosure"][open]').forEach((details) => {
        details.open = false;
      });
    }

    _onMegaToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.matches('details[is="mega-menu-disclosure"]')) return;
      if (details.open && this.open) this.hide();
    }

    _onToggleClick(event) {
      const control = event.target.closest(`[aria-controls="${this.id}"]`);
      if (!control) return;
      event.preventDefault();
      event.stopPropagation();
      this.open ? this.hide() : this.show();
    }

    _onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        event.preventDefault();
        this.hide();
      }
    }

    _onBackdropClick() {
      this.hide();
    }

    _onCloseClick(event) {
      event.preventDefault();
      this.hide();
    }

    _onSectionSelect(event) {
      this.show(!event.detail?.load);
    }

    _onSectionDeselect() {
      this.hide();
    }
  }

  customElements.define('yq-search-panel', YqSearchPanel);
})();
